import type {
    AccountSubtype,
    AccountType,
} from "@common/constants/domains/accounting/accounts";
import type { FinancialAccountType } from "@common/types/financialAccount";

export enum ImportedAccountMappingResolution {
    EXISTING_ACCOUNT = "existing-account",
    NEW_ACCOUNT = "new-account",
    CONNECT_IN_TASKS = "connect-in-tasks",
    UNMAPPED = "unmapped",
}

export interface ImportedAccountMappingResolutionFields {
    pendingAccountId: string | null;
    createNewAccount: boolean;
    kickType: AccountType | null;
    kickSubtype: AccountSubtype | null;
    financialType: FinancialAccountType | null;
}

interface GetMappingResolutionParams {
    account: ImportedAccountMappingResolutionFields;
}

/**
 * Single source of truth for whether an imported account is ready for
 * accounts-mapping finalization. Connect-in-tasks rows are resolved even
 * though their kickType and connection task are only materialized at
 * finalization.
 */
export function getImportedAccountMappingResolution({
    account,
}: GetMappingResolutionParams): ImportedAccountMappingResolution {
    if (account.createNewAccount && account.kickType) {
        return ImportedAccountMappingResolution.NEW_ACCOUNT;
    }

    if (account.pendingAccountId !== null && !account.createNewAccount) {
        return ImportedAccountMappingResolution.EXISTING_ACCOUNT;
    }

    if (account.financialType !== null && !account.createNewAccount) {
        return ImportedAccountMappingResolution.CONNECT_IN_TASKS;
    }

    return ImportedAccountMappingResolution.UNMAPPED;
}

export function isImportedAccountMappingResolved(
    params: GetMappingResolutionParams,
): boolean {
    return (
        getImportedAccountMappingResolution(params) !==
        ImportedAccountMappingResolution.UNMAPPED
    );
}

export interface ImportedAccountMappingCounts {
    mapped: number;
    newAccounts: number;
    connectInTasks: number;
    notMapped: number;
}

export function countImportedAccountMappingResolutions({
    accounts,
}: {
    accounts: ImportedAccountMappingResolutionFields[];
}): ImportedAccountMappingCounts {
    const counts: ImportedAccountMappingCounts = {
        mapped: 0,
        newAccounts: 0,
        connectInTasks: 0,
        notMapped: 0,
    };
    for (const account of accounts) {
        switch (getImportedAccountMappingResolution({ account })) {
            case ImportedAccountMappingResolution.EXISTING_ACCOUNT:
                counts.mapped += 1;
                break;
            case ImportedAccountMappingResolution.NEW_ACCOUNT:
                counts.newAccounts += 1;
                break;
            case ImportedAccountMappingResolution.CONNECT_IN_TASKS:
                counts.connectInTasks += 1;
                break;
            case ImportedAccountMappingResolution.UNMAPPED:
                counts.notMapped += 1;
                break;
        }
    }
    return counts;
}
